import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency, STAGE_ORDER } from '../../utils/formatters';

const COLORS = [
  '#1e3a5f', '#c9952a', '#2d7a4f', '#c0392b',
  '#8e44ad', '#16a085', '#d35400', '#2980b9',
  '#27ae60', '#e74c3c',
];

function advancedBeyondPIF(deal) {
  return STAGE_ORDER.indexOf(deal.current_stage) > 0;
}

export default function CountryAnalysisView({ deals, fund }) {
  const [womenOnly, setWomenOnly] = useState(false);
  const [countryNames, setCountryNames] = useState({});
  const [sortCol, setSortCol] = useState('count');
  const [sortDir, setSortDir] = useState('desc');

  // Fetch country name registry once — same pattern as Phase 2 DealRecordPage
  useEffect(() => {
    getDocs(collection(db, 'countries')).then(snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data().name || d.id; });
      setCountryNames(map);
    }).catch(() => {});
  }, []);

  const filtered = womenOnly ? deals.filter(d => d.is_women_led === true) : deals;
  const grouped = {};
  filtered.forEach(d => {
    const k = d.company_country || 'Unknown';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(d);
  });

  const currency = fund?.base_currency || '';
  const allStats = Object.entries(grouped).map(([code, cDeals]) => {
    const totalAmount = cDeals.reduce((s, d) => s + (d.requested_amount || 0), 0);
    const scores = cDeals.filter(d => d.pif_score != null).map(d => d.pif_score);
    const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : '—';
    const advanced = cDeals.filter(advancedBeyondPIF).length;
    const womenLed = cDeals.filter(d => d.is_women_led === true).length;
    return {
      code,
      country: countryNames[code] || code,
      count: cDeals.length,
      totalAmount,
      currency,
      avgScore,
      advRate: cDeals.length > 0 ? Math.round((advanced / cDeals.length) * 100) : 0,
      womenLed,
      womenLedPct: cDeals.length > 0 ? Math.round((womenLed / cDeals.length) * 100) : 0,
    };
  });
  const pieData = [...allStats].sort((a, b) => b.count - a.count)
    .map(r => ({ name: r.country, value: r.count }));
  const rows = allStats.sort((a, b) => {
    const av = a[sortCol]; const bv = b[sortCol];
    return sortDir === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
  });

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  }
  const th = (col, label) => (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col)}>
      {label} {sortCol === col ? (sortDir === 'desc' ? '↓' : '↑') : ''}
    </th>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={womenOnly} onChange={e => setWomenOnly(e.target.checked)} />
          Show only women-led companies
        </label>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Deal Count by Country</h2></div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} deals`, 'Count']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h2>Country Breakdown</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {th('country', 'Country')}
                {th('count', 'Deals')}
                {th('totalAmount', 'Total Requested')}
                {th('avgScore', 'Avg PIF Score')}
                {th('advRate', 'Advancement Rate')}
                {th('womenLed', 'Women-Led')}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.code}>
                  <td style={{ fontWeight: 500 }}>{r.country}</td>
                  <td>{r.count}</td>
                  <td style={{ fontSize: 12 }}>{formatCurrency(r.totalAmount, r.currency)}</td>
                  <td>{r.avgScore}</td>
                  <td>{r.advRate}%</td>
                  <td>{r.womenLed} <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>({r.womenLedPct}%)</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
